import type { MindUPLesson } from "./k-2";

const NOISE_PATTERNS: RegExp[] = [
  /© The Goldie Hawn Foundation[\s\S]*?G\s*(?:3-5|6-8)\s*/gi,
  /--\s*\d+\s+of\s+\d+\s*--/gi,
  /\bUnit\s+\d+\s*\|?\s*Lesson\s+\d+\s*\|?\s*G\s*(?:3-5|6-8)\b/gi,
  /\bLesson\s+\d+\s*[:\-]?\s*/gi,
];

const STOP_MARKERS = [
  "Getting Started with the Lesson",
  "Making Connections",
  "Following Up",
  "MindUP Portfolio",
  "Mindful Me Portfolio",
  "Compassion Practice",
  "MindUP Unit",
  "Including Diverse Learners",
  "MindUP Literature List",
  "Lesson Simplification Ideas",
  "Lesson Challenger Ideas",
];

export function cleanCurriculumText(value: unknown): string {
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => (typeof item === "string" ? item : ""))
      .filter(Boolean)
      .join(" ");
    return cleanCurriculumText(joined);
  }

  if (typeof value !== "string") return "";

  let text = value.replace(/\u00a0/g, " ");
  for (const pattern of NOISE_PATTERNS) {
    text = text.replace(pattern, " ");
  }

  return text.replace(/\s+/g, " ").trim();
}

export function splitListItems(value: unknown): string[] {
  const text = cleanCurriculumText(value);
  if (!text) return [];

  // First pass: split explicit bullets.
  const bulletSplit = text.split(/[•●]/g).map((item) => item.trim()).filter(Boolean);
  let items = bulletSplit.length > 1 ? bulletSplit : [text];

  // Second pass: split pipe-delimited lesson IDs from extracted headers.
  if (items.length === 1 && items[0].includes("|")) {
    items = items[0].split("|").map((item) => item.trim()).filter(Boolean);
  }

  return items
    .map((item) =>
      truncateAtStopMarkers(
        item
        .replace(/^(MindUP for Home and Life|MindUP Portfolio|Mindful Me Portfolio)\s*:?/i, "")
        .replace(/^Concepts?\s*:?/i, "")
        .replace(/^Students\s+will\s*:?/i, "")
        .trim(),
      ),
    )
    .filter(Boolean);
}

export function splitLiteratureItems(value: unknown): string[] {
  const text = cleanCurriculumText(value);
  if (!text) return [];

  const TITLE_WORD_HINTS = new Set([
    "a",
    "an",
    "the",
    "because",
    "what",
    "how",
    "come",
    "can",
    "you",
    "your",
    "our",
    "one",
    "each",
    "every",
    "ordinary",
    "no",
    "dear",
    "talk",
    "paths",
    "some",
    "somewhere",
    "channel",
    "kids",
    "strange",
    "have",
    "giant",
    "steps",
    "world",
    "for",
    "from",
    "into",
    "peace",
    "slacker",
    "its",
    "it’s",
  ]);

  const isNameLikeToken = (token: string): boolean =>
    /^[A-Z][a-z.'’\-]+$/.test(token) || /^[A-Z]\.?$/.test(token);

  const splitAuthorAndNextTitle = (segment: string): [string, string] => {
    const tokens = segment.split(/\s+/).filter(Boolean);
    if (tokens.length <= 1) return [segment, ""];

    let bestIndex = Math.min(2, tokens.length - 1);
    let bestScore = Number.NEGATIVE_INFINITY;
    const hasAmpersandAuthor = tokens.slice(0, 5).includes("&");
    const maxAuthorWords = Math.min(hasAmpersandAuthor ? 5 : 3, tokens.length - 1);

    for (let i = 1; i <= maxAuthorWords; i++) {
      const authorTokens = tokens.slice(0, i);
      const titleTokens = tokens.slice(i);
      if (titleTokens.length === 0) continue;

      let score = 0;
      for (const token of authorTokens) {
        const lower = token.toLowerCase();
        if (isNameLikeToken(token)) score += 2;
        else score -= 2;
        if (TITLE_WORD_HINTS.has(lower)) score -= 4;
      }

      const titleFirst = titleTokens[0].toLowerCase();
      if (TITLE_WORD_HINTS.has(titleFirst)) score += 3;
      if (/^[A-Z]/.test(titleTokens[0])) score += 1;

      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }

    const author = tokens.slice(0, bestIndex).join(" ").trim();
    const nextTitle = tokens.slice(bestIndex).join(" ").trim();
    return [author, nextTitle];
  };

  const withoutAppendix = text.replace(
    /\b(?:K-2|3-5|6-8|3–5|6–8)\s+Appendix\b[\s\S]*$/i,
    "",
  );

  const normalized = truncateAtStopMarkers(
    withoutAppendix
    .replace(/^MindUP\s+Literature\s+List\s*:?/i, "")
    .replace(/\[object\s+Object\]/gi, "")
    .replace(/\bCompassion\s+Practice\b[\s\S]*$/i, "")
    .trim(),
  );

  const dashParts = normalized.split(/\s[–-]\s/).map((part) => cleanCurriculumText(part));
  if (dashParts.length >= 3) {
    const items: string[] = [];
    let currentTitle = dashParts[0];

    for (let i = 1; i < dashParts.length; i++) {
      const segment = dashParts[i];
      if (!segment) continue;

      if (i === dashParts.length - 1) {
        const finalItem = cleanCurriculumText(`${currentTitle} – ${segment}`)
          .replace(/^[,:;.\s]+/, "")
          .replace(/\s+\d+\s*$/g, "")
          .trim();
        if (finalItem) items.push(finalItem);
        break;
      }

      const [author, nextTitle] = splitAuthorAndNextTitle(segment);
      const item = cleanCurriculumText(`${currentTitle} – ${author}`)
        .replace(/^[,:;.\s]+/, "")
        .replace(/\s+\d+\s*$/g, "")
        .trim();
      if (item) items.push(item);
      currentTitle = nextTitle || currentTitle;
    }

    if (items.length >= 2) {
      return items;
    }
  }

  // Fallback to generic list splitting for already-well-formed arrays.
  return splitListItems(normalized);
}

export function sanitizePurposeText(value: unknown): string {
  const text = cleanCurriculumText(value);
  if (!text) return "";

  const marker =
    /(Concepts?\s*(?:[:•]|\u25cf)|Lesson\s+Objectives|Social\s+and\s+Emotional(?:\s+Learning)?(?:\s+Links|\s+Connections)?|Materials|Estimated\s+Time|Brain\s+Link|Getting\s+Started(?:\s+with\s+the\s+Lesson)?|Step\s+1|Making\s+Connections|Following\s+Up|Including\s+Diverse\s+Learners|Lesson\s+Simplification\s+Ideas|Lesson\s+Challenger\s+Ideas)/i;
  const markerMatch = marker.exec(text);
  if (!markerMatch) return text;

  return text.slice(0, markerMatch.index).trim();
}

export function sanitizeBrainLinkText(value: unknown): string {
  const text = cleanCurriculumText(value);
  if (!text) return "";

  // Remove repeated section labels when source extraction includes headings.
  let cleaned = text.replace(/^Brain\s+Link\s*:?/i, "").trim();

  const marker =
    /\b(Getting\s+Started(?:\s+with\s+the\s+Lesson)?|Step\s+1|Making\s+Connections|Following\s+Up|MindUP\s+for\s+Home\s+and\s+Life|Mind(?:ful)?UP?\s+Portfolio|Including\s+Diverse\s+Learners|Lesson\s+Simplification\s+Ideas|Lesson\s+Challenger\s+Ideas|MindUP\s+Literature\s+List)\b/i;
  const markerMatch = marker.exec(cleaned);
  if (markerMatch) {
    cleaned = cleaned.slice(0, markerMatch.index).trim();
  }

  // Drop section-title remnants (e.g., "111 Fostering an Optimistic View").
  cleaned = cleaned.replace(/^\d+\s+/, "").trim();
  const wordCount = cleaned.split(/\s+/).filter(Boolean).length;
  const hasSentencePunctuation = /[.!?]/.test(cleaned);
  if (wordCount <= 12 && !hasSentencePunctuation) {
    return "";
  }

  return cleaned;
}

export function sanitizePortfolioText(value: unknown): string {
  const text = cleanCurriculumText(value);
  if (!text) return "";

  // Full-curriculum extraction often dumps an entire lesson into this field.
  // If it starts with an estimated-time header, prefer omitting it.
  if (/^Estimated\s+Time\s*:/i.test(text)) {
    return "";
  }

  let cleaned = text
    .replace(/^Mind(?:ful)?UP?\s+Portfolio\s*:?/i, "")
    .replace(/^Mindful\s+Me\s+Portfolio\s*:?/i, "")
    .replace(/\[object\s+Object\]/gi, "")
    .trim();

  const marker =
    /\b(Brain\s+Link|Getting\s+Started(?:\s+with\s+the\s+Lesson)?|Step\s+1|Making\s+Connections|Following\s+Up|MindUP\s+for\s+Home\s+and\s+Life|Including\s+Diverse\s+Learners|Lesson\s+Simplification\s+Ideas|Lesson\s+Challenger\s+Ideas|MindUP\s+Literature\s+List)\b/i;
  const markerMatch = marker.exec(cleaned);
  if (markerMatch) {
    cleaned = cleaned.slice(0, markerMatch.index).trim();
  }

  return cleaned;
}

export function extractSection(source: string, start: RegExp, endPatterns: RegExp[]): string {
  const match = start.exec(source);
  if (!match) return "";

  const startIndex = match.index + match[0].length;
  const afterStart = source.slice(startIndex);
  let endIndex = afterStart.length;

  for (const pattern of endPatterns) {
    const endMatch = pattern.exec(afterStart);
    if (endMatch && endMatch.index < endIndex) {
      endIndex = endMatch.index;
    }
  }

  return cleanCurriculumText(afterStart.slice(0, endIndex));
}

export function buildLessonSourceText(rawLesson: unknown): string {
  const textFragmentsFromValue = (value: unknown): string[] => {
    if (value === null || value === undefined) return [];
    if (typeof value === "string") return [value];
    if (typeof value === "number") return [String(value)];
    if (Array.isArray(value)) {
      return value.flatMap((item) => textFragmentsFromValue(item));
    }
    if (typeof value === "object") {
      return Object.values(value as Record<string, unknown>).flatMap((item) =>
        textFragmentsFromValue(item),
      );
    }
    return [];
  };

  const lesson = rawLesson as Record<string, unknown>;
  const content = (lesson.content ?? {}) as Record<string, unknown>;
  const followingUp = (content.followingUp ?? {}) as Record<string, unknown>;

  const chunks: unknown[] = [
    lesson.title,
    lesson.purpose,
    lesson.concepts,
    lesson.objectives,
    lesson.socialEmotionalLearning,
    lesson.materials,
    content.brainLink,
    content.gettingStarted,
    content.activities,
    content.makingConnections,
    content.simplificationIdeas,
    content.challengerIdeas,
    content.literatureList,
    followingUp.mindupForHomeAndLife,
    followingUp.mindfulMePortfolio,
  ];

  const fragments = chunks.flatMap((chunk) => textFragmentsFromValue(chunk));
  return cleanCurriculumText(fragments.join(" "));
}

export function deriveStructuredSections(sourceText: string) {
  const conceptsText = extractSection(sourceText, /Concepts?\s*(?:[:•]|\u25cf)/i, [/Lesson\s+Objectives/i, /Social\s+and\s+Emotional/i]);
  const objectivesText = extractSection(sourceText, /Lesson\s+Objectives/i, [/Social\s+and\s+Emotional/i, /Materials/i]);
  const selText = extractSection(sourceText, /Social\s+and\s+Emotional(?:\s+Learning)?(?:\s+Links|\s+Connections)?/i, [/Materials/i, /Estimated\s+Time/i]);
  const materialsText = extractSection(sourceText, /Materials/i, [/Estimated\s+Time/i, /Brain\s+Link/i]);
  const brainLinkText = extractSection(sourceText, /Brain\s+Link/i, [/Getting\s+Started/i, /Step\s+1/i, /Making\s+Connections/i]);
  const gettingStartedText = extractSection(sourceText, /Getting\s+Started(?:\s+with\s+the\s+Lesson)?/i, [/Step\s+1/i, /Making\s+Connections/i]);
  const makingConnectionsText = extractSection(sourceText, /Making\s+Connections/i, [/Following\s+Up/i]);
  const homeAndLifeText = extractSection(sourceText, /MindUP\s+for\s+Home\s+and\s+Life/i, [/Mind(?:ful)?UP?\s+Portfolio/i, /Lesson\s+Simplification\s+Ideas/i, /Lesson\s+Challenger\s+Ideas/i]);
  const mindfulPortfolioText = extractSection(sourceText, /Mind(?:ful)?UP?\s+Portfolio|Mindful\s+Me\s+Portfolio/i, [/Lesson\s+Simplification\s+Ideas/i, /Lesson\s+Challenger\s+Ideas/i, /MindUP\s+Literature\s+List/i]);
  const simplificationText = extractSection(sourceText, /Lesson\s+Simplification\s+Ideas/i, [/Lesson\s+Challenger\s+Ideas/i, /MindUP\s+Literature\s+List/i]);
  const challengerText = extractSection(sourceText, /Lesson\s+Challenger\s+Ideas/i, [/MindUP\s+Literature\s+List/i]);
  const literatureText = extractSection(sourceText, /MindUP\s+Literature\s+List/i, []);

  return {
    concepts: splitListItems(conceptsText),
    objectives: splitListItems(objectivesText),
    socialEmotionalLearning: splitListItems(selText),
    materials: splitListItems(materialsText),
    brainLink: brainLinkText,
    gettingStarted: splitListItems(gettingStartedText),
    makingConnections: splitListItems(makingConnectionsText),
    mindupForHomeAndLife: splitListItems(homeAndLifeText),
    mindfulMePortfolio: cleanCurriculumText(mindfulPortfolioText),
    simplificationIdeas: splitListItems(simplificationText),
    challengerIdeas: splitListItems(challengerText),
    literatureList: splitLiteratureItems(literatureText),
  };
}

export function isLikelyContaminatedList(items: string[]): boolean {
  if (!items.length) return false;
  const joined = items.join(" ");
  if (
    /(Step\s+\d+|Making\s+Connections|Following\s+Up|Including\s+Diverse\s+Learners|MindUP\s+Literature\s+List|Read\s+the\s+script|©\s+The\s+Goldie\s+Hawn\s+Foundation)/i.test(
      joined,
    )
  ) {
    return true;
  }
  return items.some((item) => item.length > 700);
}

function truncateAtStopMarkers(text: string): string {
  let trimmed = text;
  for (const marker of STOP_MARKERS) {
    const idx = trimmed.toLowerCase().indexOf(marker.toLowerCase());
    if (idx >= 0) {
      trimmed = trimmed.slice(0, idx);
    }
  }
  return trimmed.trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function sanitizeActivityInstruction(
  text: string,
  lessonTitle?: string,
): string {
  let cleaned = truncateAtStopMarkers(cleanCurriculumText(text));
  if (!cleaned) return "";

  // Remove trailing citation blocks sometimes appended from lesson source text.
  const doiIndex = cleaned.search(/\bdoi:\s*10\./i);
  if (doiIndex > 0) {
    cleaned = cleaned.slice(0, doiIndex).trim();
  }

  // Drop page footer/header bleed like "91 Getting Granular about Feelings".
  if (lessonTitle) {
    const titlePattern = new RegExp(
      `\\b\\d{1,3}\\s+${escapeRegExp(lessonTitle)}\\b`,
      "i",
    );
    const titleMatch = titlePattern.exec(cleaned);
    if (titleMatch && titleMatch.index > 0) {
      cleaned = cleaned.slice(0, titleMatch.index).trim();
    }
  }

  // Strip orphaned numeric section prefixes.
  cleaned = cleaned.replace(/^\d+\s+/, "").trim();
  cleaned = cleaned.replace(/\s+\d+\s*$/, "").trim();
  cleaned = cleaned
    .replace(/\b\d{1,3}\s+Compassion\s+Practice(?:\s*\(cont\.\))?.*$/i, "")
    .replace(/\bGetting\s+Started\s+with\s+the\s+Lesson.*$/i, "")
    .trim();
  return cleaned;
}

export function normalizeActivities(
  value: unknown,
  lessonTitle?: string,
): MindUPLesson["content"]["activities"] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item, index) => {
      const activity = item as Record<string, unknown>;
      const rawInstructions = splitListItems(activity.instructions);
      const instructions = rawInstructions
        .map((instruction) =>
          sanitizeActivityInstruction(instruction, lessonTitle),
        )
        .filter(Boolean);

      return {
        step: typeof activity.step === "number" ? activity.step : index + 1,
        title: cleanCurriculumText(activity.title) || `Activity ${index + 1}`,
        instructions: instructions.length > 0 ? instructions : ["See source curriculum text."],
      };
    })
    .filter((activity) => activity.title.length > 0);
}

export function chooseFirstNonEmpty<T>(...values: T[]): T | undefined {
  for (const value of values) {
    if (Array.isArray(value)) {
      if (value.length > 0) return value;
      continue;
    }

    if (typeof value === "string") {
      if (value.trim().length > 0) return value;
      continue;
    }

    if (value) return value;
  }
  return undefined;
}
